import { BlockStack, Button, Card, Collapsible, Icon, InlineStack, Text } from '@shopify/polaris';
import { CheckIcon, XIcon } from '@shopify/polaris-icons';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComparePlanStyled } from './styled';

interface ComparePlanProps {}

interface Feature {
  name: string;
  free: string | boolean;
  basic: string | boolean;
  premium: string | boolean;
  shopifyPlus: string | boolean;
}

const mockData = (t: TFunction): Feature[] => [
  {
    name: t('pop_up'),
    free: '1',
    basic: '1',
    premium: t('unlimited'),
    shopifyPlus: t('unlimited'),
  },
  {
    name: t('pop_up_customization'),
    free: t('text_key'),
    basic: t('text_size_color'),
    premium: t('unlimited'),
    shopifyPlus: t('text_size_color_button_background'),
  },
  {
    name: t('translation_key'),
    free: false,
    basic: '1',
    premium: t('unlimited'),
    shopifyPlus: t('unlimited'),
  },
  {
    name: t('checkout_verification'),
    free: false,
    basic: t('banner_key'),
    premium: t('banner_key'),
    shopifyPlus: t('banner_key'),
  },
  {
    name: 'CSS',
    free: false,
    basic: false,
    premium: true,
    shopifyPlus: true,
  },
  {
    name: t('terms_conditions'),
    free: false,
    basic: false,
    premium: true,
    shopifyPlus: true,
  },
  {
    name: t('live_chat_support_24_7'),
    free: true,
    basic: true,
    premium: t('vip_priority'),
    shopifyPlus: t('vip_priority'),
  },
];

const ComparePlan: React.FC<ComparePlanProps> = () => {
  const [isShow, setIsShow] = useState(false);
  const { t } = useTranslation('pricing');

  const renderCell = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <span className="icon-feature-check">
          <Icon tone="caution" source={CheckIcon} />
        </span>
      ) : (
        <Icon source={XIcon} tone="critical" />
      );
    }
    return <Text as="p">{value}</Text>;
  };

  const dataCompare: Feature[] = mockData(t);

  return (
    <ComparePlanStyled>
      <BlockStack gap="500">
        <Card>
          <InlineStack align="space-between" blockAlign="start" gap="200">
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                {t('compare_plan_features')}
              </Text>
              <Text as="p" tone="subdued">
                {t('blockify_age_verification_features')}
              </Text>
            </BlockStack>

            <Button size="large" onClick={() => setIsShow(!isShow)}>
              {isShow ? t('show_less') : t('show_more')}
            </Button>
          </InlineStack>
        </Card>

        <Collapsible open={isShow} id="" transition={{ duration: '500ms' }}>
          <div className="plan-table">
            <div className="table-row">
              <div className="table-cell"></div>
              <div className="table-cell text-center text-bold">FREE</div>
              <div className="table-cell text-center text-bold">BASIC</div>
              <div className="table-cell text-center text-bold">PREMIUM</div>
            </div>
            {dataCompare.map((feature, index) => (
              <div key={index} className="table-row">
                <div className="table-cell">
                  <Text as="p">{feature.name}</Text>
                </div>
                <div className="table-cell text-center text-bold">{renderCell(feature.free)}</div>

                <div className="table-cell text-center text-bold">{renderCell(feature.basic)}</div>
                <div className="table-cell text-center text-bold">{renderCell(feature.premium)}</div>
              </div>
            ))}
          </div>
        </Collapsible>
      </BlockStack>
    </ComparePlanStyled>
  );
};

export default ComparePlan;
